import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowUp, Sparkles } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useIsTouchBand } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';
import {
  chatExplorer,
  type ChartSpec,
  type ExplorerMessage,
} from '@/services/marketingExplorer.service';

// Local message type — `charts` only present on assistant turns where the AI
// emitted a chart spec via the `render_chart` tool.
interface UiMessage extends ExplorerMessage {
  charts?: ChartSpec[];
  errored?: boolean;
}

const SUGGESTIONS = [
  'Show me last 30 days GA sessions by day',
  'Compare Google Ads and Meta spend last 30 days',
  'How many sessions did we get yesterday?',
  'Plot Meta clicks vs conversions for the last 14 days',
];

export default function MarketingExplorerPage() {
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setLoading] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // The third chat archetype in the app, and the one that had none of this: a raw
  // `100vh` box with a composer pinned to its bottom and no keyboard handling at all.
  // `vh` is the LARGE viewport — it does not shrink for collapsing browser chrome, let
  // alone for a keyboard — so this composer sat under the keyboard on every phone. Not
  // reported by a ticket; included because the AI overlay and the assistant page have
  // now each been fixed separately for the same defect, and leaving the third copy is
  // what produces the fourth report.
  useVisualViewportVars(useIsTouchBand());

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isLoading]);

  async function submit(question: string) {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;

    const userMsg: UiMessage = { role: 'user', content: trimmed };
    const history: ExplorerMessage[] = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await chatExplorer({ question: trimmed, history });
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.answer || '(empty response)', charts: res.charts || [] },
      ]);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong fetching the response.';
      setMessages((prev) => [...prev, { role: 'assistant', content: message, errored: true }]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  }

  const isEmpty = messages.length === 0;

  return (
    // The `4rem` is this page's own desktop chrome and is left exactly as it was.
    // Inside the touch band `touch-keyboard-surface` takes over the height entirely,
    // with `--kb-surface-top` set to the height of the shell's phone-only header — the
    // same 3rem the assistant page uses, and 0 above 768px where that header is hidden.
    <div className="flex h-[calc(100vh-4rem)] flex-col bg-slate-50 max-md:[--kb-surface-top:3rem] touch-keyboard-surface">
      {/* Scrollable conversation */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-4 py-8">
          {isEmpty ? (
            <EmptyState onPick={(q) => submit(q)} disabled={isLoading} />
          ) : (
            <div className="space-y-6">
              {messages.map((m, i) => (
                <MessageBubble key={i} message={m} />
              ))}
              {isLoading && <ThinkingBubble />}
            </div>
          )}
        </div>
      </div>

      {/* Input bar */}
      <div className="border-t border-slate-200 bg-white">
        <form onSubmit={onSubmit} className="mx-auto max-w-3xl px-4 py-3">
          <div className="flex items-end gap-2 rounded-xl border border-slate-300 bg-white p-2 shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask about your marketing data — e.g. 'Show me Meta spend by day for the last 30 days'"
              rows={1}
              className="flex-1 resize-none border-none bg-transparent px-2 py-1.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 max-h-40"
              disabled={isLoading}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading}
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-lg text-white transition',
                input.trim() && !isLoading
                  ? 'bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500'
                  : 'bg-slate-300 cursor-not-allowed',
              )}
              aria-label="Send"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-2 px-2 text-[11px] text-slate-400">
            Pulls live data from connected Google Analytics, Google Ads, and Meta Ads. Press Enter to send · Shift+Enter for newline.
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────

function EmptyState({ onPick, disabled }: { onPick: (q: string) => void; disabled: boolean }) {
  return (
    <div className="pt-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600">
        <Sparkles className="h-6 w-6 text-white" />
      </div>
      <h1 className="mt-4 text-2xl font-semibold text-slate-900">Marketing AI Explorer</h1>
      <p className="mt-1 text-sm text-slate-500">
        Ask anything about Google Analytics, Google Ads, or Meta — get answers and charts.
      </p>

      <div className="mt-8 grid gap-2 sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <Button
            key={s}
            type="button"
            variant="secondary"
            disabled={disabled}
            className="h-auto justify-start whitespace-normal py-3 text-left text-sm"
            onClick={() => onPick(s)}
          >
            {s}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ─── Message bubble ─────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: UiMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-indigo-600 px-4 py-2.5 text-sm text-white shadow-sm">
          <div className="whitespace-pre-wrap">{message.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div className="flex w-full max-w-[95%] gap-3">
        <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600">
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex-1 space-y-3">
          <div
            className={cn(
              'rounded-2xl rounded-tl-md border px-4 py-3 text-sm shadow-sm',
              message.errored
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-slate-200 bg-white text-slate-800',
            )}
          >
            <FormattedText text={message.content} />
          </div>
          {message.charts?.map((c) => (
            <ChartCard key={c.id} spec={c} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="flex justify-start">
      <div className="flex gap-3">
        <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600">
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="flex items-center gap-2 rounded-2xl rounded-tl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <Spinner className="h-4 w-4 text-indigo-600" />
          <span className="text-sm text-slate-500">Thinking...</span>
        </div>
      </div>
    </div>
  );
}

// ─── Minimal markdown: **bold** + line breaks ───────────────────────────────

function FormattedText({ text }: { text: string }) {
  // Split paragraphs on blank lines, render **bold** within each.
  const paragraphs = text.split(/\n{2,}/);
  return (
    <div className="space-y-2">
      {paragraphs.map((para, pi) => (
        <p key={pi} className="whitespace-pre-wrap leading-relaxed">
          {renderInline(para)}
        </p>
      ))}
    </div>
  );
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

// ─── Chart renderer ─────────────────────────────────────────────────────────

function ChartCard({ spec }: { spec: ChartSpec }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      {spec.title && (
        <div className="mb-3 text-sm font-semibold text-slate-800">{spec.title}</div>
      )}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart(spec)}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function renderChart(spec: ChartSpec): React.ReactElement {
  const { type, xKey, yKeys, data } = spec;
  const common = { data, margin: { top: 5, right: 16, left: 0, bottom: 5 } };

  if (type === 'bar') {
    return (
      <BarChart {...common}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {yKeys.map((y) => (
          <Bar key={y.key} dataKey={y.key} name={y.label} fill={y.color} />
        ))}
      </BarChart>
    );
  }

  if (type === 'area') {
    return (
      <AreaChart {...common}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {yKeys.map((y) => (
          <Area
            key={y.key}
            type="monotone"
            dataKey={y.key}
            name={y.label}
            stroke={y.color}
            fill={y.color}
            fillOpacity={0.2}
          />
        ))}
      </AreaChart>
    );
  }

  // default: line
  return (
    <LineChart {...common}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
      <YAxis tick={{ fontSize: 11 }} />
      <Tooltip />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      {yKeys.map((y) => (
        <Line
          key={y.key}
          type="monotone"
          dataKey={y.key}
          name={y.label}
          stroke={y.color}
          strokeWidth={2}
          dot={{ r: 2 }}
        />
      ))}
    </LineChart>
  );
}
